import {useMemo, useState} from "react";
import {Alert, Select, Table, Tag, Tooltip} from "antd";
import className from "classnames/bind";
import {Button, ExportButtons, FontAwesomeIcon} from "~/components";
import {useGetCompetitorCompareQuery} from "~/reduxs/api/competitorApiSlice";
import CompareDetail from "./CompareDetail";
import {GRADE_COLORS} from "./gradeMeta";
import style from "../style/Competitor.module.scss";

const cn = className.bind(style);

/**
 * Tab "So sánh SEO" — bảng side-by-side điểm on-page: cột = site của bạn + từng đối thủ,
 * hàng = tổng điểm + từng nhóm tiêu chí (Kỹ thuật/Meta/Xã hội/Nội dung/Hình ảnh/Cấu trúc/
 * Bảo mật). Ô tốt nhất/kém nhất mỗi hàng được tô màu. Đọc snapshot ĐÃ chấm (tab Đối thủ)
 * — không tự tốn lượt. Drill-down: chọn 2 cột đã có báo cáo để so từng tiêu chí.
 */
function CompetitorCompare({projectId}) {

	const {data, isFetching} = useGetCompetitorCompareQuery(projectId);

	const [leftId, setLeftId] = useState(null);   // report_id bên trái (thường own)
	const [rightId, setRightId] = useState(null); // report_id bên phải

	const cols = useMemo(() => (data?.rows || []).filter(Boolean), [data]);
	const withReport = useMemo(() => cols.filter((c) => c.report), [cols]);

	// Hàng nhóm tiêu chí: hợp nhất groups từ mọi summary (phòng khi bộ tiêu chí đổi giữa 2 lần chấm).
	const groupRows = useMemo(() => {
		const seen = new Map();
		withReport.forEach((c) => (c.report.summary || []).forEach((g) => {
			if (!seen.has(g.key)) seen.set(g.key, g.label);
		}));
		return [...seen.entries()].map(([key, label]) => ({key, label}));
	}, [withReport]);

	if (!isFetching && withReport.length === 0) {
		return (
			<Alert type="info" showIcon
				message="Chưa có báo cáo nào để so sánh"
				description='Sang tab "Đối thủ" chấm điểm website của bạn và ít nhất 1 đối thủ trước.' />
		);
	}

	// % điểm nhóm của 1 cột (null nếu cột chưa có báo cáo/nhóm đó).
	const groupPercent = (col, groupKey) => {
		const g = (col.report?.summary || []).find((x) => x.key === groupKey);
		if (!g || !g.max) return null;
		return Math.round((g.score / g.max) * 100);
	};

	// Dữ liệu bảng: hàng đầu là tổng điểm, sau đó từng nhóm.
	const rows = [
		{key: '__total', label: 'Tổng điểm', isTotal: true},
		...groupRows.map((g) => ({key: g.key, label: g.label})),
	];

	const cellValue = (row, col) => (row.isTotal ? (col.report ? col.report.score : null) : groupPercent(col, row.key));

	const bestWorst = (row) => {
		const values = cols.map((c) => cellValue(row, c)).filter((v) => v !== null);
		if (values.length < 2) return {best: null, worst: null};
		const best = Math.max(...values);
		const worst = Math.min(...values);
		return {best, worst: worst !== best ? worst : null};
	};

	const tableColumns = [
		{title: 'Nhóm tiêu chí', dataIndex: 'label', key: 'label', fixed: 'left', width: 170,
			render: (v, row) => row.isTotal ? <strong>{v}</strong> : v},
		...cols.map((c) => ({
			title: (
				<div>
					<div style={{fontWeight: 600}}>{c.competitor_id === 0 ? <><FontAwesomeIcon icon="fa-light fa-house-chimney" /> {c.name}</> : c.name}</div>
					<div className={cn('hint')}>{c.domain}</div>
					{c.report
						? (c.report.stale && <Tooltip title="Bản chấm đã quá 24h — chấm lại ở tab Đối thủ."><Tag color="orange">cũ</Tag></Tooltip>)
						: <Tag>chưa chấm</Tag>}
				</div>
			),
			key: `col-${c.competitor_id}`,
			align: 'center',
			width: 150,
			onCell: (row) => {
				const v = cellValue(row, c);
				const {best, worst} = bestWorst(row);
				if (v === null || best === null) return {};
				if (v === best) return {className: cn('bestCell')};
				if (worst !== null && v === worst) return {className: cn('worstCell')};
				return {};
			},
			render: (_, row) => {
				const v = cellValue(row, c);
				if (v === null) return <span className={cn('hint')}>—</span>;
				if (row.isTotal) {
					return (
						<span className={cn('scoreCell')}>
							<Tag color={GRADE_COLORS[c.report.grade] || 'default'}>{c.report.grade}</Tag>{v}
						</span>
					);
				}
				return `${v}%`;
			},
		})),
	];

	const buildExcel = () => [{
		name: 'So sánh SEO',
		aoa: [
			['Nhóm tiêu chí', ...cols.map((c) => `${c.name} (${c.domain})`)],
			['Tổng điểm', ...cols.map((c) => c.report ? c.report.score : '')],
			['Xếp hạng', ...cols.map((c) => c.report ? c.report.grade : '')],
			...groupRows.map((g) => [g.label, ...cols.map((c) => {
				const v = groupPercent(c, g.key);
				return v === null ? '' : `${v}%`;
			})]),
			['Chấm lúc', ...cols.map((c) => c.report?.created || '')],
		],
	}];

	const buildPdf = () => ({
		title: 'So sánh SEO on-page với đối thủ',
		subtitle: cols.map((c) => c.domain).filter(Boolean).join(' · '),
		sections: [{
			columns: ['Nhóm tiêu chí', ...cols.map((c) => c.name)],
			rows: [
				['Tổng điểm', ...cols.map((c) => c.report ? `${c.report.score} (${c.report.grade})` : '—')],
				...groupRows.map((g) => [g.label, ...cols.map((c) => {
					const v = groupPercent(c, g.key);
					return v === null ? '—' : `${v}%`;
				})]),
			],
		}],
	});

	const detailOptions = withReport.map((c) => ({value: c.report.id, label: `${c.name} (${c.domain})`}));

	return (
		<div>
			<div className={cn('actionBar')}>
				<span className={cn('hint')}>
					Điểm nhóm tính theo % đạt của nhóm tiêu chí — ô xanh: tốt nhất hàng, ô đỏ: kém nhất hàng.
				</span>
				<ExportButtons filename={`so-sanh-doi-thu-${projectId}`} buildExcel={buildExcel} buildPdf={buildPdf} />
			</div>

			<Table
				rowKey="key"
				size="small"
				loading={isFetching}
				columns={tableColumns}
				dataSource={rows}
				scroll={{x: 'max-content'}}
				pagination={false}
			/>

			{withReport.length >= 2 && (
				<div className={cn('section')} style={{marginTop: 18}}>
					<h3 className={cn('sectionTitle')}>
						<FontAwesomeIcon icon="fa-light fa-code-compare" /> So từng tiêu chí (drill-down)
					</h3>
					<div className={cn('filterBar')}>
						<Select style={{minWidth: 260, maxWidth: 380}} placeholder="Cột gốc (thường là site của bạn)"
							options={detailOptions} value={leftId} onChange={setLeftId} />
						<FontAwesomeIcon icon="fa-light fa-arrow-right-arrow-left" style={{color: '#9ca3af'}} />
						<Select style={{minWidth: 260, maxWidth: 380}} placeholder="Cột so sánh (đối thủ)"
							options={detailOptions} value={rightId} onChange={setRightId} />
						{leftId && rightId && leftId !== rightId && (
							<Button small white onClick={() => { setLeftId(null); setRightId(null); }}
								leftIcon={<FontAwesomeIcon icon="fa-light fa-xmark" />}>Đóng</Button>
						)}
					</div>

					{leftId && rightId && leftId !== rightId && (
						<CompareDetail projectId={projectId} leftReportId={leftId} rightReportId={rightId} />
					)}
				</div>
			)}
		</div>
	);
}

export default CompetitorCompare;
